import { ROLE_LABELS, type Role } from '@/shared/api';

/**
 * Teksten van inloggen, omgeving, wachtwoord en uitnodiging. Binnen de app is
 * alles **jij-vorm**; alleen de publieke klantpagina gebruikt u.
 *
 * Foutmeldingen zeggen nooit of een account bestaat.
 */
export const authCopy = {
    brand: 'Smartkeur',
    claim: 'Van opname naar rapport zonder tussenstap.',
    /**
     * Wat Smartkeur nu doet, zonder wat later komt (`../../docs/scope.md`). Alleen wat vandaag
     * waar is: geen cijfers die niemand heeft gemeten.
     */
    claimBody: 'Voor bouwtechnische keuringen volgens NTA 8060, van de afspraak tot de factuur.',
    bullets: [
        'De opname werkt offline op de tablet, ook in de kruipruimte',
        'Foto’s koppelen zichzelf aan het bouwdeel',
        'Rapport met NHG-bijlage, voor de klant als link en als pdf',
    ],
    legal: 'Smartkeur B.V.',

    email: 'E-mail',
    password: 'Wachtwoord',
    backToLogin: 'Terug naar inloggen',
    goToLogin: 'Naar inloggen',

    /* Inloggen */
    loginTitle: 'Inloggen',
    /** Tablet en telefoon krijgen van de API 14 dagen, een computer 12 uur. */
    deviceNoticeField: 'Dit apparaat blijft 14 dagen ingelogd.',
    deviceNoticeOffice: 'Op deze computer blijf je 12 uur ingelogd.',
    submit: 'Inloggen',
    submitBusy: 'Bezig met inloggen…',
    forgotLink: 'Wachtwoord vergeten?',
    /** Naar het aanmeldformulier op de website (`WEBSITE_SIGNUP_URL`). */
    signupLink: 'Nog geen account? Probeer 2 keuringen gratis',

    emailRequired: 'Vul je e-mailadres in.',
    emailInvalid: 'Dit lijkt geen e-mailadres.',
    passwordRequired: 'Vul je wachtwoord in.',
    /** Zegt bewust niet of het account bestaat. */
    credentialsWrong: 'E-mailadres of wachtwoord klopt niet.',
    rateLimited: (seconds: number) =>
        seconds > 1
            ? `Te veel pogingen achter elkaar. Probeer het over ${seconds} seconden opnieuw.`
            : 'Te veel pogingen achter elkaar. Probeer het zo opnieuw.',
    offline: 'Geen verbinding met Smartkeur. Controleer je internet en probeer het opnieuw.',
    loginFailed: 'Inloggen lukt nu niet. Probeer het zo opnieuw.',

    /* Waarom je weer op het inlogscherm staat */
    sessionExpired: 'Je sessie is verlopen. Log opnieuw in om verder te werken.',
    /** Het apparaat moet zich opnieuw melden (vertrouwen verlopen, onbekend): opnieuw inloggen werkt. */
    deviceUnavailable:
        'Dit apparaat was te lang niet bevestigd, dus je bent uitgelogd. Log opnieuw in met je wachtwoord om verder te werken.',
    /**
     * Uitgelogd omdat dit apparaat is ingetrokken. Opnieuw inloggen helpt hier niet
     * tot het weer is toegelaten, dus dat zeggen we ook niet.
     */
    deviceRevokedNotice:
        'Dit apparaat is ingetrokken, door jezelf of door een beheerder, en is daarom uitgelogd. Werk verder op een ander apparaat. Wil je dit apparaat terug, laat het dan daar onder Account weer toe; daarna kun je hier weer inloggen.',
    /** Inloggen geweigerd omdat dit apparaat is ingetrokken. */
    deviceRevoked:
        'Dit apparaat is ingetrokken, door jezelf of door een beheerder, dus inloggen lukt hier nu niet. Werk verder op een ander apparaat. Wil je dit apparaat terug, laat het dan daar onder Account weer toe; daarna kun je hier weer inloggen.',

    /* Omgeving */
    tenantTitle: 'Kies je omgeving',
    tenantBody: 'Je werkt voor meerdere keuringsbedrijven. Kies waar je nu aan werkt.',
    tenantNotice: 'Later wisselen kan altijd, onderaan het menu naast je naam.',
    tenantSwitchTitle: 'Wisselen van omgeving',
    tenantSwitchBody: 'Alles wat je hierna doet gebeurt in de omgeving die je kiest.',
    tenantCurrent: 'Je werkt nu in',
    tenantBusy: 'bezig',
    tenantDefault: 'standaard',
    tenantSwitchHint: 'wisselen',
    tenantFailed: 'Wisselen van omgeving lukte niet. Je werkt nog in de vorige omgeving.',
    tenantDenied: 'Je hebt geen toegang meer tot die omgeving. Kies een andere.',
    tenantOffline: 'Wisselen kan alleen met verbinding. Je werkt nog in de vorige omgeving.',
    noWorkspaceTitle: 'Geen actieve omgeving',
    noWorkspaceBody:
        'Je account is bij geen enkel keuringsbedrijf actief. Vraag de eigenaar om een nieuwe uitnodiging.',
    signOut: 'Uitloggen',

    /* Wachtwoord vergeten */
    forgotTitle: 'Wachtwoord vergeten',
    forgotBody: 'Vul je e-mailadres in. Je krijgt een link om een nieuw wachtwoord te kiezen.',
    forgotSubmit: 'Herstellink versturen',
    forgotBusy: 'Bezig met versturen…',
    /** Zelfde melding voor een bestaand en een onbekend adres. */
    forgotSent:
        'Is dit adres bij ons bekend, dan staat er binnen een paar minuten een herstellink in de inbox. Kijk ook bij ongewenste mail.',

    /* Wachtwoord herstellen */
    resetTitle: 'Nieuw wachtwoord kiezen',
    resetBody: 'Kies een wachtwoord van minstens 12 tekens dat je nergens anders gebruikt.',
    newPassword: 'Nieuw wachtwoord',
    confirmPassword: 'Herhaal wachtwoord',
    resetSubmit: 'Wachtwoord opslaan',
    resetBusy: 'Bezig met opslaan…',
    resetDone: 'Je wachtwoord is ingesteld. Log in met je nieuwe wachtwoord.',
    resetInvalid: 'Deze herstellink werkt niet meer. Vraag een nieuwe aan.',
    resetIncomplete: 'Deze herstellink is onvolledig. Vraag een nieuwe aan.',
    requestNewLink: 'Nieuwe herstellink aanvragen',
    resetFailed: 'Opslaan lukt nu niet. Probeer het zo opnieuw.',
    passwordTooShort: 'Gebruik minstens 12 tekens.',
    passwordMismatch: 'De twee wachtwoorden zijn niet gelijk.',
    confirmRequired: 'Herhaal je wachtwoord.',

    /* Uitnodiging */
    invitationLoading: 'Uitnodiging ophalen…',
    invitationEyebrow: 'Uitnodiging',
    invitationTitle: (tenant: string) => `Welkom bij ${tenant}`,
    invitationTitleUnknown: 'Je bent uitgenodigd',
    invitationRole: (role: string, tenant: string) => `Je komt erbij als ${role} bij ${tenant}.`,
    invitationBy: (name: string) => `Uitgenodigd door ${name}`,
    invitationValidUntil: (date: string) => `Geldig tot ${date}`,
    /** Voor een nieuw adres én voor een account dat nog nooit een wachtwoord had. */
    invitationNewAccount:
        'Kies een wachtwoord om je account te activeren. Daarmee log je voortaan in.',
    invitationExistingAccount:
        'Je hebt al een Smartkeur-account. Accepteren voegt deze omgeving toe; je wachtwoord blijft hetzelfde.',
    invitationName: 'Naam',
    invitationNameHint: 'Zoals collega’s je in Smartkeur zien.',
    invitationAccept: 'Uitnodiging accepteren',
    invitationAcceptBusy: 'Bezig met accepteren…',
    invitationNotFound: 'Deze uitnodiging bestaat niet (meer). Controleer de link in de mail.',
    invitationExpired:
        'Deze uitnodiging is verlopen. Vraag degene die je uitnodigde om een nieuwe te sturen.',
    invitationAccepted: 'Deze uitnodiging is al gebruikt. Log in met je e-mailadres en wachtwoord.',
    invitationFailed: 'Accepteren lukt nu niet. Probeer het zo opnieuw.',
    invitationLoadFailed: 'De uitnodiging kon niet worden opgehaald. Probeer het zo opnieuw.',
    invitationMismatchTitle: 'Deze uitnodiging is voor een ander account',
    invitationMismatchBody:
        'Log uit en open de link opnieuw om de uitnodiging te accepteren, of log in met het account waarvoor hij bedoeld is.',
    invitationInvited: 'Uitgenodigd',
    invitationSignedInAs: 'Ingelogd als',
    invitationSignOut: 'Uitloggen',
    invitationSigningOut: 'Bezig met uitloggen…',
    invitationDisabledTitle: 'Dit account is uitgeschakeld',
    invitationDisabled:
        'Het account van dit e-mailadres is uitgeschakeld, dus deze uitnodiging kan nu niet worden geaccepteerd. Vraag degene die je uitnodigde om het account weer in te schakelen.',
    retry: 'Opnieuw proberen',

    /*
     * Activeren: de welkomstvariant van de uitnodiging, voor wie zich net op de
     * website aanmeldde (`/activeren/:token`). Hij is niet uitgenodigd, hij vroeg
     * er zelf om.
     */
    activationEyebrow: 'Account activeren',
    activationLoading: 'Je account ophalen…',
    activationTitle: 'Welkom bij Smartkeur, kies je wachtwoord',
    activationBody: (tenant: string) =>
        `Daarmee activeer je je account en open je de omgeving van ${tenant}, met je gratis keuringen erin.`,
    activationTitleUnknown: 'Welkom bij Smartkeur',
    activationAccept: 'Account activeren',
    activationAcceptBusy: 'Bezig met activeren…',
    activationNotFound:
        'Deze activatielink werkt niet (meer). Gebruik de link uit de nieuwste mail, of meld je opnieuw aan op de website.',
    activationExpired:
        'Deze activatielink is verlopen. Meld je opnieuw aan op de website met hetzelfde e-mailadres; je krijgt dan een nieuwe link voor hetzelfde bedrijf.',
    activationAccepted: 'Je account is al geactiveerd. Log in met je e-mailadres en wachtwoord.',
    activationFailed: 'Activeren lukt nu niet. Probeer het zo opnieuw.',
    activationLoadFailed: 'De activatielink kon niet worden gecontroleerd. Probeer het zo opnieuw.',
    /** Bij een verlopen of onbekende activatielink: opnieuw aanmelden geeft een nieuwe. */
    activationSignupAgain: 'Opnieuw aanmelden op de website',
} as const;

export function roleAtTenant(roleLabel: string, tenantName: string): string {
    return `${roleLabel} bij ${tenantName}`;
}

/** De rollen in één omgeving, leesbaar: "Inspecteur" of "Eigenaar, Inspecteur". */
export function rolesLabel(roles: readonly Role[]): string {
    return roles.map((role) => ROLE_LABELS[role]).join(', ');
}
